import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LockAutomationRequest,
  LockAutomationResponse,
} from "api/ApiClient";
import axios from "axios";
import React, { useState } from "react";
import { BASE_API_URL } from "store/Me";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  statusBadgeOutlineClass,
  statusBadgeVariantFromColor,
} from "shared/utils/statusChipColor";

const AUTOMATIONS_QUERY_KEY = (lockId: string) => [
  "locks",
  "automations",
  lockId,
];

const AUTOMATION_ACTIONS = [
  { value: "pass", label: "Pass" },
  { value: "sec", label: "Secure" },
];

const AUTOMATION_ACTION_DESCRIPTIONS: Record<string, string> = {
  pass: "Set lock to pass mode at the scheduled time.",
  sec: "Set lock to secure mode at the scheduled time.",
};

const DAY_PRESETS = [
  { key: "everyday", label: "Every Day" },
  { key: "weekdays", label: "Weekdays" },
  { key: "weekend", label: "Weekend" },
  { key: "clear", label: "Clear" },
] as const;

const TIME_PRESETS = [
  { key: "morning", label: "Morning", value: "08:00" },
  { key: "noon", label: "Noon", value: "12:00" },
  { key: "evening", label: "Evening", value: "17:00" },
] as const;

type DayPresetKey = (typeof DAY_PRESETS)[number]["key"];

const DAYS: { key: keyof LockAutomationRequest; label: string }[] = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

function minutesToTimeString(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}:${m}`;
}

function timeStringToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function formatTimeDisplay(minutes: number | undefined): string {
  if (minutes === undefined) return "—";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function formatDays(automation: LockAutomationResponse): string {
  const days: string[] = [];
  if (automation.sunday) days.push("Sun");
  if (automation.monday) days.push("Mon");
  if (automation.tuesday) days.push("Tue");
  if (automation.wednesday) days.push("Wed");
  if (automation.thursday) days.push("Thu");
  if (automation.friday) days.push("Fri");
  if (automation.saturday) days.push("Sat");
  return days.join(", ") || "—";
}

function formatRelativeDate(date: Date | null): string {
  if (!date) return "";

  const diffMs = date.getTime() - Date.now();
  const minutes = Math.max(1, Math.round(Math.abs(diffMs) / 60000));
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (minutes < 60) {
    return diffMs >= 0 ? `In ${minutes} min` : `${minutes} min ago`;
  }

  if (hours < 24) {
    return diffMs >= 0 ? `In ${hours} hr` : `${hours} hr ago`;
  }

  return diffMs >= 0 ? `In ${days} day` : `${days} day ago`;
}

function formatDateInTimeZone(date: Date | null, timeZone: string): string {
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getEnabledDayIndexes(automation: LockAutomationResponse): number[] {
  const indexes: number[] = [];
  if (automation.sunday) indexes.push(0);
  if (automation.monday) indexes.push(1);
  if (automation.tuesday) indexes.push(2);
  if (automation.wednesday) indexes.push(3);
  if (automation.thursday) indexes.push(4);
  if (automation.friday) indexes.push(5);
  if (automation.saturday) indexes.push(6);
  return indexes;
}

function getNextRunDate(automation: LockAutomationResponse): Date | null {
  if (automation.time === undefined) return null;
  const enabledDays = getEnabledDayIndexes(automation);
  if (enabledDays.length === 0) return null;

  const now = new Date();
  const hours = Math.floor(automation.time / 60);
  const minutes = automation.time % 60;

  for (let offset = 0; offset < 8; offset += 1) {
    const candidate = new Date(now);
    candidate.setSeconds(0, 0);
    candidate.setDate(now.getDate() + offset);
    if (!enabledDays.includes(candidate.getDay())) {
      continue;
    }

    candidate.setHours(hours, minutes, 0, 0);
    if (candidate > now) {
      return candidate;
    }
  }

  return null;
}

function getAutomationStatus(
  automation: LockAutomationResponse,
): { label: string; color: "success" | "primary" | "default" } {
  const enabledDays = getEnabledDayIndexes(automation);
  if (enabledDays.length === 0) {
    return { label: "Inactive", color: "default" };
  }

  const nextRun = getNextRunDate(automation);
  if (!nextRun) {
    return { label: "Scheduled", color: "default" };
  }

  const hoursUntilNext = (nextRun.getTime() - Date.now()) / 3600000;
  if (hoursUntilNext <= 24) {
    return { label: "Upcoming", color: "primary" };
  }

  return { label: "Scheduled", color: "success" };
}

function formatAction(action: string | undefined): string {
  if (action === "pass") return "Pass";
  if (action === "sec") return "Secure";
  return action ?? "—";
}

export default function LockAutomation({
  lockId,
  siteTimeZone,
}: {
  lockId: string;
  siteTimeZone?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] =
    useState<LockAutomationResponse | null>(null);
  const [copySourceAutomation, setCopySourceAutomation] =
    useState<LockAutomationResponse | null>(null);

  const { data, isError, isLoading } = useGetLockAutomations(lockId);
  const showEmptyState = !!data && data.length === 0;
  const timezone = siteTimeZone || "America/Chicago";
  const sortedAutomations = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const aTime = a.time ?? Number.MAX_SAFE_INTEGER;
      const bTime = b.time ?? Number.MAX_SAFE_INTEGER;
      if (aTime !== bTime) {
        return aTime - bTime;
      }

      const aDays = formatDays(a);
      const bDays = formatDays(b);
      return aDays.localeCompare(bDays);
    });
  }, [data]);

  const openCreate = () => {
    setEditingAutomation(null);
    setCopySourceAutomation(null);
    setModalOpen(true);
  };

  const openEdit = (automation: LockAutomationResponse) => {
    setEditingAutomation(automation);
    setCopySourceAutomation(null);
    setModalOpen(true);
  };

  const openCopy = (automation: LockAutomationResponse) => {
    setEditingAutomation(null);
    setCopySourceAutomation(automation);
    setModalOpen(true);
  };

  if (isError) return <div>Error loading automations</div>;
  if (isLoading) return <div>Loading...</div>;

  return (
    <div style={{ paddingBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <p style={{ margin: 0, color: "#666", fontSize: 13 }}>
          Use automation scheduling to automatically lock and unlock doors each
          week at a specified time. Changes take effect on the next lock sync.
          Times are shown in {timezone}.
        </p>
        {!showEmptyState ? (
          <Button type="button" onClick={openCreate}>
            Create Automation
          </Button>
        ) : null}
      </div>

      {showEmptyState ? (
        <div
          style={{
            border: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            borderRadius: 8,
            padding: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, color: "#111827", marginBottom: 4 }}>
              No automations yet
            </div>
            <div style={{ color: "#6b7280", fontSize: 13 }}>
              Create your first automation to schedule pass or secure actions.
            </div>
          </div>
          <Button type="button" onClick={openCreate}>
            Create Automation
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-3">
              <thead className="text-xs font-semibold border-b border-slate-200 bg-slate-50 text-slate-500">
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">Action</th>
                  <th scope="col">Time</th>
                  <th scope="col">Days</th>
                  <th scope="col">Next Run</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100 text-slate-700 [&>tr:hover]:bg-slate-50">
                {sortedAutomations.map((automation) => (
                  <AutomationRow
                    key={automation.id}
                    automation={automation}
                    lockId={lockId}
                    onEdit={() => openEdit(automation)}
                    onCopy={() => openCopy(automation)}
                    timezone={timezone}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <AutomationFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        lockId={lockId}
        automation={editingAutomation}
        copySourceAutomation={copySourceAutomation}
        automations={data ?? []}
      />
    </div>
  );
}

function AutomationRow({
  automation,
  lockId,
  onEdit,
  onCopy,
  timezone,
}: {
  automation: LockAutomationResponse;
  lockId: string;
  onEdit: () => void;
  onCopy: () => void;
  timezone: string;
}) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: deleteAutomation, isPending: isDeleting } = useMutation({
    mutationFn: () =>
      axios.delete(
        `${BASE_API_URL}/api/Locks/${lockId}/automations/${automation.id}`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AUTOMATIONS_QUERY_KEY(lockId),
      });
      setConfirmOpen(false);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Error deleting automation.";
      setDeleteError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });
  const automationStatus = getAutomationStatus(automation);

  return (
    <>
      <tr>
        <td>
          <Badge
            className={
              automationStatus.label === "Inactive"
                ? statusBadgeOutlineClass(automationStatus.color)
                : undefined
            }
            variant={
              automationStatus.label === "Inactive"
                ? "outline"
                : statusBadgeVariantFromColor(automationStatus.color)
            }
          >
            {automationStatus.label}
          </Badge>
        </td>
        <td>{formatAction(automation.action)}</td>
        <td>{formatTimeDisplay(automation.time)}</td>
        <td>{formatDays(automation)}</td>
        <td>
          <div>{formatDateInTimeZone(getNextRunDate(automation), timezone)}</div>
          <div style={{ color: "#6b7280", fontSize: 12 }}>
            {formatRelativeDate(getNextRunDate(automation))}
          </div>
        </td>
        <td>
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" type="button" variant="outline" onClick={onEdit}>
              Edit
            </Button>
            <Button size="sm" type="button" variant="outline" onClick={onCopy}>
              Copy
            </Button>
            <Button
              className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                setDeleteError(null);
                setConfirmOpen(true);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              Delete
            </Button>
          </div>
        </td>
      </tr>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          showCloseButton={false}
          className="sm:max-w-xs"
        >
          <DialogHeader>
            <DialogTitle>Delete Automation</DialogTitle>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              {deleteError}
            </Alert>
          )}
          Are you sure you want to delete this <strong>{formatAction(automation.action)}</strong> automation?
          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
            {formatTimeDisplay(automation.time)} on {formatDays(automation)}
          </div>
          <DialogFooter>
            <Button
              disabled={isDeleting}
              onClick={() => setConfirmOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isDeleting}
              onClick={() => deleteAutomation()}
              type="button"
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const defaultDays = {
  sunday: true,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
};

function AutomationFormModal({
  open,
  onClose,
  lockId,
  automation,
  copySourceAutomation,
  automations,
}: {
  open: boolean;
  onClose: () => void;
  lockId: string;
  automation: LockAutomationResponse | null;
  copySourceAutomation: LockAutomationResponse | null;
  automations: LockAutomationResponse[];
}) {
  const queryClient = useQueryClient();
  const isEditing = automation !== null;

  const [action, setAction] = useState("pass");
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState<Record<string, boolean>>(defaultDays);
  const [error, setError] = useState<string | null>(null);

  // Sync form when editing automation changes
  React.useEffect(() => {
    if (automation) {
      setAction(automation.action ?? "pass");
      setTime(
        automation.time !== undefined
          ? minutesToTimeString(automation.time)
          : "08:00",
      );
      setDays({
        sunday: automation.sunday ?? false,
        monday: automation.monday ?? false,
        tuesday: automation.tuesday ?? false,
        wednesday: automation.wednesday ?? false,
        thursday: automation.thursday ?? false,
        friday: automation.friday ?? false,
        saturday: automation.saturday ?? false,
      });
    } else if (copySourceAutomation) {
      setAction(copySourceAutomation.action ?? "pass");
      setTime(
        copySourceAutomation.time !== undefined
          ? minutesToTimeString(copySourceAutomation.time)
          : "08:00",
      );
      setDays({
        sunday: copySourceAutomation.sunday ?? false,
        monday: copySourceAutomation.monday ?? false,
        tuesday: copySourceAutomation.tuesday ?? false,
        wednesday: copySourceAutomation.wednesday ?? false,
        thursday: copySourceAutomation.thursday ?? false,
        friday: copySourceAutomation.friday ?? false,
        saturday: copySourceAutomation.saturday ?? false,
      });
    } else {
      setAction("pass");
      setTime("08:00");
      setDays(defaultDays);
    }
    setError(null);
  }, [automation, copySourceAutomation, open]);

  const validationError = React.useMemo(() => {
    if (!time) {
      return "Please select a valid time.";
    }

    const hasDaySelected = Object.values(days).some(Boolean);
    if (!hasDaySelected) {
      return "Select at least one day.";
    }

    return null;
  }, [days, time]);

  const overlapWarning = React.useMemo(() => {
    const selectedDays = new Set(
      Object.entries(days)
        .filter(([, enabled]) => enabled)
        .map(([day]) => day),
    );

    const selectedTime = timeStringToMinutes(time);

    const hasConflict = automations.some((existing) => {
      if (isEditing && existing.id === automation?.id) {
        return false;
      }

      if (existing.time !== selectedTime) {
        return false;
      }

      const existingDays: string[] = [];
      if (existing.sunday) existingDays.push("sunday");
      if (existing.monday) existingDays.push("monday");
      if (existing.tuesday) existingDays.push("tuesday");
      if (existing.wednesday) existingDays.push("wednesday");
      if (existing.thursday) existingDays.push("thursday");
      if (existing.friday) existingDays.push("friday");
      if (existing.saturday) existingDays.push("saturday");

      return existingDays.some((day) => selectedDays.has(day));
    });

    return hasConflict
      ? "This rule overlaps another automation at the same time and day."
      : null;
  }, [automations, automation?.id, days, isEditing, time]);

  const actionDescription = AUTOMATION_ACTION_DESCRIPTIONS[action] ?? "";

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: LockAutomationRequest) =>
      isEditing
        ? axios.put(
            `${BASE_API_URL}/api/Locks/${lockId}/automations/${automation!.id}`,
            payload,
          )
        : axios.post(
            `${BASE_API_URL}/api/Locks/${lockId}/automations`,
            payload,
          ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AUTOMATIONS_QUERY_KEY(lockId),
      });
      onClose();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "An error occurred saving the automation.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  const isSubmitDisabled = isPending || !!validationError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutate({
      action,
      time: timeStringToMinutes(time),
      sunday: days.sunday,
      monday: days.monday,
      tuesday: days.tuesday,
      wednesday: days.wednesday,
      thursday: days.thursday,
      friday: days.friday,
      saturday: days.saturday,
    });
  };

  const toggleDay = (key: string) => {
    setDays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const applyDayPreset = (preset: DayPresetKey) => {
    if (preset === "everyday") {
      setDays({
        sunday: true,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
      });
    }

    if (preset === "weekdays") {
      setDays({
        sunday: false,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: false,
      });
    }

    if (preset === "weekend") {
      setDays({
        sunday: true,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: true,
      });
    }

    if (preset === "clear") {
      setDays({
        sunday: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Automation" : "Create Automation"}
            </DialogTitle>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              {error}
            </Alert>
          )}
          {validationError && (
            <Alert variant="warning">
              {validationError}
            </Alert>
          )}
          {overlapWarning && (
            <Alert variant="info">
              {overlapWarning}
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="automation-action">Action</Label>
            <Select
              value={action}
              onValueChange={setAction}
            >
              <SelectTrigger id="automation-action" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUTOMATION_ACTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              {actionDescription}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {TIME_PRESETS.map((preset) => (
              <Button
                key={preset.key}
                onClick={() => setTime(preset.value)}
                size="sm"
                type="button"
                variant="outline"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="automation-time">Time</Label>
            <Input
              id="automation-time"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              required
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {DAY_PRESETS.map((preset) => (
              <Button
                key={preset.key}
                onClick={() => applyDayPreset(preset.key)}
                size="sm"
                type="button"
                variant="outline"
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            {DAYS.map((d) => (
              <div className="flex items-center gap-2" key={d.key}>
                <Checkbox
                  id={`automation-day-${d.key}`}
                  checked={!!days[d.key as string]}
                  onCheckedChange={() => toggleDay(d.key as string)}
                />
                <Label htmlFor={`automation-day-${d.key}`}>{d.label}</Label>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button disabled={isPending} onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {isPending ? "Saving..." : isEditing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function getLockAutomations(
  lockId: string,
): Promise<LockAutomationResponse[]> {
  const response = await axios.get<LockAutomationResponse[]>(
    `${BASE_API_URL}/api/Locks/${lockId}/automations`,
  );
  return response.data;
}

function useGetLockAutomations(lockId: string) {
  return useQuery({
    queryKey: AUTOMATIONS_QUERY_KEY(lockId),
    queryFn: () => getLockAutomations(lockId),
  });
}
